import { useState } from "react";
import { toast } from "sonner";
import { Loader2, MessageCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { SlotPicker } from "@/components/BookingWizard";
import { CATALOG } from "@/lib/catalog";
import { api, errorText, waLink, fmtDate } from "@/lib/api";
import { field } from "@/components/admin/shared";

const L = ({ label, children }) => <label className="block text-xs font-semibold text-mute">{label}<div className="mt-1">{children}</div></label>;

const Form = ({ a, onSaved }) => {
  const [f, setF] = useState({ name: a.name, phone: a.phone, vehicle: a.vehicle, plate: a.plate || "", service: a.service, notes: a.notes || "", date: a.date, time: a.time });
  const [override, setOverride] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null);
  const set = (p) => setF({ ...f, ...p });
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/appointments/${a.id}`, { ...f, override_capacity: override });
      toast.success("Cita actualizada");
      setSaved(data);
      onSaved();
    } catch (e) { toast.error(errorText(e)); }
    setBusy(false);
  };
  if (saved) {
    const moved = saved.date !== a.date || saved.time !== a.time;
    const msg = `Hola ${saved.name}, te escribimos del Taller de Suspensiones La Gringa. ${moved ? "Tu cita fue reprogramada para" : "Te confirmamos tu cita del"} ${fmtDate(saved.date)} a las ${saved.time} (${saved.service}). Calle 136 #715-A, Mérida. ¡Te esperamos!`;
    return (
      <div className="space-y-4 text-sm" data-testid="appt-edit-saved">
        <p className="text-ink2">Cambios guardados{moved ? `: nueva fecha ${fmtDate(saved.date)} a las ${saved.time}` : ""}.</p>
        <a href={waLink(saved.phone, msg)} target="_blank" rel="noopener noreferrer" className="btn-gold" data-testid="appt-edit-notify-btn"><MessageCircle size={16} /> Avisar al cliente por WhatsApp</a>
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="appt-edit-form">
      <div className="grid gap-3 sm:grid-cols-2">
        <L label="Nombre"><input className={field} value={f.name} onChange={(e) => set({ name: e.target.value })} data-testid="appt-edit-name" /></L>
        <L label="Teléfono"><input className={field} value={f.phone} onChange={(e) => set({ phone: e.target.value })} data-testid="appt-edit-phone" /></L>
        <L label="Vehículo"><input className={field} value={f.vehicle} onChange={(e) => set({ vehicle: e.target.value })} data-testid="appt-edit-vehicle" /></L>
        <L label="Servicio">
          <select className={field} value={f.service} onChange={(e) => set({ service: e.target.value })} data-testid="appt-edit-service">
            {[...new Set([f.service, ...CATALOG.map((s) => s.name)])].map((s) => <option key={s}>{s}</option>)}
          </select>
        </L>
        <L label="Fecha"><input type="date" className={field} value={f.date} onChange={(e) => set({ date: e.target.value, time: "" })} data-testid="appt-edit-date" /></L>
        <L label="Notas"><input className={field} value={f.notes} onChange={(e) => set({ notes: e.target.value })} data-testid="appt-edit-notes" /></L>
      </div>
      <div><p className="mb-2 text-xs font-semibold text-mute">Hora {f.time && <b className="text-green">· {f.time}</b>}</p><SlotPicker date={f.date} value={f.time} onChange={(time) => set({ time })} showAll /></div>
      <label className="flex items-center gap-2 text-sm text-mute"><Checkbox checked={override} onCheckedChange={(v) => setOverride(!!v)} data-testid="appt-edit-override" /> Permitir aunque el horario esté lleno o fuera de horario</label>
      <button className="btn-gold" disabled={busy || !f.time} onClick={save} data-testid="appt-edit-save-btn">{busy && <Loader2 size={16} className="animate-spin" />} Guardar cambios</button>
    </div>
  );
};

export const EditAppointmentDialog = ({ appt, onClose, onSaved }) => (
  <Dialog open={!!appt} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto bg-white">
      <DialogHeader><DialogTitle className="font-display text-2xl font-extrabold">Modificar o reprogramar cita</DialogTitle></DialogHeader>
      {appt && <Form key={appt.id} a={appt} onSaved={onSaved} />}
    </DialogContent>
  </Dialog>
);
